import { MouseEventHandler, useRef } from "react";

import Slider, { Settings } from "react-slick";

import { isNonEmptyArray } from "@enkor/toss-slash";

import { StarIcon } from "@enkor/design/icons";
import { pink } from "@enkor/design/colors";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { useFetchHostReviewListQuery } from "@domains/event/network/eventQueries";
import WebViewBridge from "@logics/utils/webViewBridge";
import { usePreventVerticalScroll } from "@logics/hooks/usePreventVerticalScroll";

import ReviewCard from "./ReviewCard/ReviewCard";

import * as S from "./ReviewSection.style";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const sliderSettings: Settings = {
  dots: false,
  infinite: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: false,
  arrows: false,
  speed: 250,
  touchThreshold: 100,
  variableWidth: true,
  cssEase: "linear",
};

interface ReviewSectionProps {
  userId: string;
  eventId: string;
}

const ReviewSection = ({ userId, eventId }: ReviewSectionProps) => {
  const sliderContainerRef = useRef<HTMLDivElement>(null);

  const { data: hostReviewList } = useFetchHostReviewListQuery(eventId);
  const hasReviewList =
    hostReviewList?.reviewList && isNonEmptyArray(hostReviewList.reviewList);

  const handleViewAllReview: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (WebViewBridge.isInAppWebView()) {
      e.preventDefault();
      WebViewBridge.getInstance().navigateToProfileReview(userId);
    }
  };

  usePreventVerticalScroll<HTMLDivElement>(sliderContainerRef);

  return (
    <S.Container>
      <S.AvgIndicator>
        <StarIcon size={32} fillColor={pink.main} strokeColor="transparent" />
        <S.AvgText name="h3">{hostReviewList?.avgRating}</S.AvgText>
      </S.AvgIndicator>

      {hasReviewList && (
        <S.ReviewCardListWrapper ref={sliderContainerRef}>
          <Slider {...sliderSettings}>
            {hostReviewList.reviewList
              .filter(
                ({ review }) => !!review.content || !!review.fileList?.[0]?.id,
              ) // NOTE: content가 존재하거나 이미지가 있는 경우에만 이벤트 리스트에 포함.
              .map(({ id, review, reviewer, event }) => (
                <ReviewCard
                  key={id}
                  userId={userId}
                  id={id}
                  review={review}
                  reviewer={reviewer}
                  event={event}
                />
              ))}
          </Slider>
        </S.ReviewCardListWrapper>
      )}

      <S.UnderlineLink
        href={`${NAVIGATION_ROUTE.PROFILE.HREF}/${userId}/review`}
        onClick={handleViewAllReview}
      >
        View {hostReviewList?.totalCount} review
        {(hostReviewList?.totalCount || 0) > 1 ? "s" : ""}
      </S.UnderlineLink>
    </S.Container>
  );
};

export default ReviewSection;
